import { ref, watch } from 'vue'
import Notification from '~/services/notificationService'
import { useNotificationFeedStore } from '~/stores/notificationFeed'
import { useUserStore } from '~/stores/user'
import type { UserModule } from '~/types'

const ONE_MINUTE = 60 * 1000
const TEN_MINUTES = 10 * ONE_MINUTE
const TWENTY_MINUTES = 20 * ONE_MINUTE
const STARTUP_JITTER = 30 * 1000
export const UPDATE_TIME_KEY = 'pwa-update-time'

function formatTimeRemaining(milliseconds: number): string {
  if (milliseconds <= 0) return 'now'
  const minutes = Math.floor(milliseconds / 60000)
  const seconds = Math.floor((milliseconds % 60000) / 1000)
  return `${minutes}m ${seconds}s`
}

/**
 * Creates an isolated update controller with its own timer and notification state.
 * Exported so stories and unit tests can drive the countdown logic without a service worker.
 */
export function createPwaUpdateController() {
  // Tracks the bell-feed notification ID (authenticated path only).
  // Null until a notification is shown; stays null on the logged-out path.
  const notificationId = ref<string | null>(null)

  // Separate flag for the logged-out path because ElMessage returns id: '' (falsy),
  // which would fool the !notificationId check into showing a new toast every tick.
  let loggedOutToastShown = false

  let countdownInterval: number | null = null
  let updateTimeout: number | null = null

  function clearUpdateState() {
    if (countdownInterval !== null) {
      window.clearInterval(countdownInterval)
      countdownInterval = null
    }
    if (updateTimeout !== null) {
      window.clearTimeout(updateTimeout)
      updateTimeout = null
    }
    notificationId.value = null
    loggedOutToastShown = false
    localStorage.removeItem(UPDATE_TIME_KEY)
  }

  function triggerUpdate(updateSW: () => Promise<void>) {
    clearUpdateState()
    updateSW()
  }

  function startCountdown(updateSW: () => Promise<void>) {
    let targetTime: number
    const storedUpdateTime = localStorage.getItem(UPDATE_TIME_KEY)
    if (storedUpdateTime) {
      targetTime = Number.parseInt(storedUpdateTime)
    } else {
      targetTime = Date.now() + TEN_MINUTES
      localStorage.setItem(UPDATE_TIME_KEY, targetTime.toString())
    }

    // Reset timers — handles re-entry from a second onNeedRefresh event
    if (countdownInterval !== null) {
      window.clearInterval(countdownInterval)
      countdownInterval = null
    }
    if (updateTimeout !== null) {
      window.clearTimeout(updateTimeout)
      updateTimeout = null
    }

    const timeRemaining = targetTime - Date.now()
    if (timeRemaining <= 0) {
      triggerUpdate(updateSW)
      return
    }

    if (useUserStore().loggedIn) {
      // Authenticated: single bell-feed entry with a live per-second countdown
      if (!notificationId.value) {
        const handle = Notification.show({
          title: 'Update Available',
          message: `Click here to update Dray Dog. Auto-update will occur in ${formatTimeRemaining(timeRemaining)}.`,
          type: 'warning',
          duration: 0,
          onClick: () => triggerUpdate(updateSW),
          showClose: false,
        })
        notificationId.value = handle.id
      }
      countdownInterval = window.setInterval(() => {
        const remaining = targetTime - Date.now()
        if (remaining <= 0) {
          triggerUpdate(updateSW)
          return
        }
        if (notificationId.value) {
          Notification.updateNotification(notificationId.value, {
            message: `Click here to update Dray Dog. Auto-update will occur in ${formatTimeRemaining(remaining)}.`,
          })
        }
      }, 1000)
    } else {
      // Logged out (terminal cameras, login, sign-up): one static toast, no per-second interval.
      // Per-second ElMessage calls stack because ElMessage returns id: '' (falsy), so the
      // !notificationId guard never fires — see DEV-6952. Using loggedOutToastShown avoids that.
      if (!loggedOutToastShown) {
        Notification.show({
          title: 'Update Available',
          message:
            'A Dray Dog update is available. Dismiss this notification to update now.',
          type: 'warning',
          duration: 0,
          showClose: true,
          onClose: () => triggerUpdate(updateSW),
        })
        loggedOutToastShown = true
      }
      updateTimeout = window.setTimeout(() => {
        triggerUpdate(updateSW)
      }, timeRemaining)
    }
  }

  return {
    startCountdown,
    clearUpdateState,
    triggerUpdate,
    /** Exposed for the feed-dismissal watcher in install(). */
    notificationId,
  }
}

// https://github.com/antfu/vite-plugin-pwa#automatic-reload-when-new-content-available
export const install: UserModule = ({ isClient, router }) => {
  if (!isClient) return
  const controller = createPwaUpdateController()
  const feedStore = useNotificationFeedStore()

  // When the notification is removed from the bell feed (user dismissed it),
  // stop the countdown so it doesn't tick in the background.
  watch(
    () => feedStore.notifications,
    (notifications) => {
      if (
        controller.notificationId.value &&
        !notifications.some((n) => n.id === controller.notificationId.value)
      ) {
        controller.clearUpdateState()
      }
    }
  )

  router
    .isReady()
    .then(async () => {
      const { registerSW } = await import('virtual:pwa-register')
      // Jitter so that all clients don't update at the same time
      const randomJitter = Math.floor(Math.random() * STARTUP_JITTER)
      const updateSW = registerSW({
        immediate: true,
        onRegistered(registration) {
          if (registration) {
            setInterval(() => {
              registration.update()
            }, ONE_MINUTE + randomJitter)
          }
        },
        onNeedRefresh() {
          const storedUpdateTime = localStorage.getItem(UPDATE_TIME_KEY)
          if (storedUpdateTime) {
            const targetTime = Number.parseInt(storedUpdateTime)
            if (Date.now() < targetTime) {
              controller.startCountdown(updateSW)
            } else {
              controller.triggerUpdate(updateSW)
            }
          } else {
            // Spread updates over 20 minutes to avoid thundering herd
            const randomDelay = Math.floor(Math.random() * TWENTY_MINUTES)
            setTimeout(() => {
              controller.startCountdown(updateSW)
            }, randomDelay)
          }
        },
      })
    })
    .catch(() => {})
}
